<template>
    <v-container class="fill-height pa-4" fluid>
        <v-row justify="center" align="center">
            <v-col cols="12" sm="9" md="6" lg="4" xl="3">
                <v-card class="pa-6" rounded="lg" elevation="3">
                    <div class="text-overline grey--text mb-3">DG Ventures</div>

                    <!-- loading -->
                    <div v-if="state === 'loading'" class="text-center py-10">
                        <v-progress-circular indeterminate color="primary" size="36" />
                    </div>

                    <!-- link expired / invalid -->
                    <template v-else-if="state === 'expired'">
                        <div class="text-h4 mb-2">⏰</div>
                        <div class="text-h6 font-weight-bold mb-1">This link has expired</div>
                        <p class="text-body-2 grey--text text--darken-1 mb-0">
                            Approve / Decline links are valid for 24 hours.
                        </p>
                    </template>

                    <!-- generic failure -->
                    <template v-else-if="state === 'error'">
                        <div class="text-h4 mb-2">⚠️</div>
                        <div class="text-h6 font-weight-bold mb-1">Something went wrong</div>
                        <p class="text-body-2 grey--text text--darken-1 mb-0">
                            {{ errorMessage || 'We couldn’t load this request.' }}
                        </p>
                    </template>

                    <!-- already decided -->
                    <template v-else-if="state === 'decided'">
                        <div class="text-h4 mb-2">☑️</div>
                        <div class="text-h6 font-weight-bold mb-1">
                            Already {{ data.status === 'approved' ? 'approved' : 'declined' }}
                        </div>
                        <p class="text-body-2 grey--text text--darken-1 mb-0">
                            {{ data.request.name }}'s account request was already
                            {{ data.status === 'approved' ? 'approved' : 'declined' }}. No further action is needed.
                        </p>
                    </template>

                    <!-- decision recorded -->
                    <template v-else-if="state === 'done'">
                        <div class="text-h4 mb-2">{{ result === 'approved' ? '✅' : '❌' }}</div>
                        <div class="text-h6 font-weight-bold mb-1">
                            {{ result === 'approved' ? 'Approved' : 'Declined' }}
                        </div>
                        <p class="text-body-2 grey--text text--darken-1 mb-0">
                            {{ data.request.name }}'s account request has been {{ result }}.
                            <template v-if="result === 'approved'">They've been emailed a password and a login link.</template>
                        </p>
                    </template>

                    <!-- confirm -->
                    <template v-else-if="state === 'confirm'">
                        <div class="text-h6 font-weight-bold mb-1">
                            {{ isApprove ? 'Approve' : 'Decline' }} this account request?
                        </div>

                        <v-simple-table dense class="aac-table mb-4">
                            <tbody>
                                <tr>
                                    <td class="grey--text">Name</td>
                                    <td class="text-right font-weight-medium">{{ data.request.name }}</td>
                                </tr>
                                <tr>
                                    <td class="grey--text">Email</td>
                                    <td class="text-right font-weight-medium">{{ data.request.email }}</td>
                                </tr>
                                <tr>
                                    <td class="grey--text">Phone</td>
                                    <td class="text-right font-weight-medium">{{ data.request.phone }}</td>
                                </tr>
                                <tr>
                                    <td class="grey--text">Role</td>
                                    <td class="text-right font-weight-medium">{{ data.request.role }}</td>
                                </tr>
                                <tr>
                                    <td class="grey--text">Company</td>
                                    <td class="text-right font-weight-medium">{{ data.request.company }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>

                        <v-btn :color="isApprove ? 'green darken-2' : 'red darken-2'" dark depressed block
                            class="text-none" :loading="submitting" @click="submit">
                            Confirm {{ isApprove ? 'approval' : 'decline' }}
                        </v-btn>
                    </template>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
export default {
    name: 'AccountActionComponent',
    data() {
        return {
            state: 'loading', // loading | confirm | decided | done | expired | error
            data: null,
            submitting: false,
            result: null,
            errorMessage: '',
        };
    },
    computed: {
        id() {
            return this.$route.params.id;
        },
        decision() {
            return this.$route.params.decision;
        },
        isApprove() {
            return this.decision === 'approve';
        },
        // The signed capability token, forwarded verbatim to the API.
        query() {
            return window.location.search;
        },
        endpoint() {
            return `/api/account-requests/action/${this.id}/${this.decision}`;
        },
    },
    async mounted() {
        if (!['approve', 'decline'].includes(this.decision)) {
            this.state = 'error';
            return;
        }

        const res = await this.$axios.get(this.endpoint + this.query);
        this.applyResponse(res, (data) => {
            this.data = data;
            this.state = data.status === 'pending' ? 'confirm' : 'decided';
        });
    },
    methods: {
        applyResponse(res, onOk) {
            if (res && res.status === 200 && res.data) {
                onOk(res.data);
                return;
            }
            if (res && res.status === 403 && res.data && res.data.error === 'expired') {
                this.state = 'expired';
                return;
            }
            this.errorMessage = (res && res.data && res.data.message) || '';
            this.state = 'error';
        },
        async submit() {
            this.submitting = true;
            const res = await this.$axios.post(this.endpoint + this.query, {});
            this.submitting = false;

            this.applyResponse(res, (data) => {
                this.data = data;
                if (data.already) {
                    this.state = 'decided';
                } else {
                    this.result = data.status;
                    this.state = 'done';
                }
            });
        },
    },
};
</script>

<style scoped>
.aac-table >>> td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    height: 38px;
}
</style>
